import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, FormControl, FormLabel, Modal, ModalBody, ModalCloseButton, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Stack, Switch, Text,
    useColorModeValue,
} from "@chakra-ui/react";
import RateField from "./RateField";
import { createCurrencyRate, updateCurrencyRate } from "./currencyRatesApi";
import { DEFAULT_MARGIN_PERCENT, KNOWN_CURRENCIES } from "./currencyRatesConstants";
import { directionLabel } from "./seedPairs";

/**
 * Builds the form state for a creation, or from the row being edited.
 * @param {Object|null} rate - normalized rate, or null when creating
 * @returns {Object}
 */
function buildForm(rate) {
    if (!rate) {
        return { base: "EUR", quote: "MGA", rate: "", margin: DEFAULT_MARGIN_PERCENT, active: true };
    }
    return {
        base: rate.baseCurrency, quote: rate.quoteCurrency,
        rate: rate.rate, margin: rate.marginPercent, active: rate.active,
    };
}

/**
 * Validates the form before submission.
 * @param {Object} form - form state
 * @param {boolean} isEdit - whether an existing row is being updated
 * @returns {string|null} error message, or null when valid
 */
function validateForm(form, isEdit) {
    const value = Number(form.rate);
    if (!form.rate || Number.isNaN(value) || value <= 0) {
        return "Le taux doit être un nombre strictement positif";
    }
    if (!isEdit && form.base === form.quote) return "Les deux devises doivent être différentes";
    return null;
}

/**
 * Sends the create or update request.
 * @param {Object} form - form state
 * @param {Object|null} rate - row being edited, or null when creating
 * @returns {Promise<Object>} the persisted row
 */
async function persist(form, rate) {
    if (rate) {
        return updateCurrencyRate(rate.id, {
            rate: form.rate, margin_percent: form.margin, active: form.active,
        });
    }
    return createCurrencyRate({
        base_currency: form.base, quote_currency: form.quote,
        rate: form.rate, margin_percent: form.margin,
    });
}

/**
 * Submits the form and surfaces the outcome.
 * @param {Object} args - form state, edited row and the surrounding setters
 */
async function submit({ form, rate, setLoading, onSaved, onClose }) {
    const invalid = validateForm(form, Boolean(rate));
    if (invalid) {
        toast.error(invalid);
        return;
    }
    setLoading(true);
    try {
        await persist(form, rate);
        toast.success(rate ? "Taux mis à jour" : "Direction créée");
        onSaved?.();
        onClose();
    } catch (err) {
        const message = err?.response?.status === 409
            ? "Cette direction existe déjà : modifie-la au lieu d'en créer une seconde"
            : err?.response?.data;
        toast.error(typeof message === "string" ? message : "Échec de l'enregistrement");
    } finally {
        setLoading(false);
    }
}

/**
 * Currency pickers, shown on creation only: the direction is immutable
 * afterwards because the pair carries a unique constraint server-side.
 * @param {Object} props - form state and the field setter factory
 * @returns {React.ReactElement}
 */
function DirectionPickers({ form, setField }) {
    return (
        <SimpleGrid columns={2} gap="12px">
            <RateField label="Devise de départ" isRequired>
                <Select value={form.base} onChange={(e) => setField("base")(e.target.value)} borderRadius="12px">
                    {KNOWN_CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
                </Select>
            </RateField>
            <RateField label="Devise d'arrivée" isRequired>
                <Select value={form.quote} onChange={(e) => setField("quote")(e.target.value)} borderRadius="12px">
                    {KNOWN_CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
                </Select>
            </RateField>
        </SimpleGrid>
    );
}

/**
 * Create or edit one directional rate. On edit the direction itself is locked:
 * the reverse sense is a separate row, so that a margin is never applied
 * against the platform by inverting an existing rate.
 * @param {Object} props - isOpen, onClose, the edited rate and onSaved
 * @returns {React.ReactElement}
 */
function RateFormDialog({ isOpen, onClose, rate, onSaved }) {
    const [form, setForm] = useState(buildForm(rate));
    const [loading, setLoading] = useState(false);
    const isEdit = Boolean(rate);
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

    useEffect(() => {
        if (isOpen) setForm(buildForm(rate));
    }, [isOpen, rate]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="16px">
                <ModalHeader fontSize="md">
                    {isEdit ? `Modifier ${directionLabel(form.base, form.quote)}` : "Ajouter une direction"}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing="14px">
                        {isEdit ? (
                            <Text fontSize="xs" color={hintColor}>
                                La direction est immuable : pour le sens inverse, crée une autre ligne.
                                Les paiements déjà cotés gardent leur taux figé, cette modification ne
                                les repricera pas.
                            </Text>
                        ) : (
                            <DirectionPickers form={form} setField={setField} />
                        )}
                        <RateField
                            label="Taux" value={form.rate} onChange={setField("rate")} isRequired
                            placeholder="5000" helper={`Combien de ${form.quote} vaut 1 ${form.base}`}
                        />
                        <RateField
                            label="Marge (%)" value={form.margin} onChange={setField("margin")}
                            placeholder="3" helper="Spread ajouté au montant du payeur"
                        />
                        {isEdit && (
                            <FormControl display="flex" alignItems="center" gap="10px">
                                <Switch
                                    isChecked={form.active}
                                    onChange={(e) => setField("active")(e.target.checked)}
                                />
                                <FormLabel mb="0" fontSize="sm">Direction active</FormLabel>
                            </FormControl>
                        )}
                        {isEdit && !form.active && (
                            <Text fontSize="xs" color="orange.400">
                                Désactiver cette direction refuse tout nouveau paiement dans ce sens :
                                il n'existe aucun repli 1:1.
                            </Text>
                        )}
                    </Stack>
                </ModalBody>
                <ModalFooter gap="10px">
                    <Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
                    <Button
                        colorScheme="brand" size="sm" isLoading={loading}
                        onClick={() => submit({ form, rate, setLoading, onSaved, onClose })}
                    >
                        {isEdit ? "Enregistrer" : "Créer"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default RateFormDialog;
